// src/components/NotFoundPage.js

import React from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    useTheme,
    useMediaQuery,
    alpha,
    Container
} from '@mui/material';
import {
    Home as HomeIcon,
    Search as SearchIcon,
    ArrowBack as ArrowBackIcon,
    Help,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const NotFoundPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Get responsive sizes based on device
    const getResponsiveSize = () => {
        if (isMobile) return { heading: 80, icon: 48, padding: 2 };
        if (isTablet) return { heading: 120, icon: 56, padding: 4 };
        return { heading: 150, icon: 64, padding: 5 };
    };

    const responsive = getResponsiveSize();

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 1, sm: 2, md: 3 },
            }}
        >
            {/* Animated Background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 30% 40%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.3) 100%)',
                    }
                }}
            />

            {/* Floating Particles Effect */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden',
                    zIndex: 1,
                }}
            >
                {[...Array(20)].map((_, i) => (
                    <Box
                        key={i}
                        component={motion.div}
                        initial={{ 
                            y: '100vh', 
                            x: `${Math.random() * 100}vw`,
                            opacity: 0.3
                        }}
                        animate={{ 
                            y: '-100vh',
                            opacity: [0.3, 0.6, 0.3]
                        }}
                        transition={{ 
                            duration: 10 + Math.random() * 15,
                            repeat: Infinity,
                            delay: Math.random() * 5
                        }}
                        sx={{
                            position: 'absolute',
                            width: { xs: 4, sm: 6, md: 8 },
                            height: { xs: 4, sm: 6, md: 8 },
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.3)',
                        }}
                    />
                ))}
            </Box>

            <Container maxWidth="sm">
                <motion.div
                    initial={{ scale: 0.8, opacity: 0, y: 30 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            zIndex: 2,
                            width: '100%',
                            maxWidth: { xs: '95%', sm: 450, md: 500 },
                            mx: 'auto',
                            p: { xs: 2, sm: 4, md: 5 },
                            borderRadius: { xs: 3, sm: 4 },
                            backgroundColor: alpha(theme.palette.background.paper, 0.95),
                            backdropFilter: 'blur(12px)',
                            textAlign: 'center',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.2)}`,
                        }}
                    >
                        {/* Animated 404 Number */}
                        <motion.div
                            animate={{ 
                                rotate: [0, 8, -8, 5, -5, 0],
                                scale: [1, 1.05, 1.05, 1],
                                transition: { duration: 0.8, delay: 0.2 }
                            }}
                        >
                            <Typography
                                variant="h1"
                                sx={{
                                    fontSize: { xs: 80, sm: 120, md: 150 },
                                    fontWeight: 'bold',
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    color: 'transparent',
                                    mb: 2,
                                    textShadow: `0 2px 10px ${alpha(theme.palette.primary.main, 0.3)}`,
                                }}
                            >
                                404
                            </Typography>
                        </motion.div>

                        {/* Animated Search Icon */}
                        <motion.div
                            animate={{ 
                                scale: [1, 1.2, 1],
                                rotate: [0, 360, 0],
                            }}
                            transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
                        >
                            <SearchIcon 
                                sx={{ 
                                    fontSize: { xs: 48, sm: 56, md: 64 }, 
                                    color: theme.palette.grey[500],
                                    mb: 2,
                                    opacity: 0.8
                                }} 
                            />
                        </motion.div>

                        <Typography 
                            variant="h4" 
                            gutterBottom 
                            sx={{ 
                                fontWeight: 'bold',
                                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                                mb: { xs: 1, sm: 2 }
                            }}
                        >
                            Page Not Found
                        </Typography>

                        <Typography 
                            variant="body1" 
                            color="text.secondary" 
                            sx={{ 
                                mb: { xs: 3, sm: 4 },
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                px: { xs: 1, sm: 2 }
                            }}
                        >
                            The page you are looking for might have been removed, had its name changed,
                            or is temporarily unavailable.
                        </Typography>

                        {/* Action Buttons */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                startIcon={<HomeIcon />}
                                onClick={() => navigate('/')}
                                sx={{
                                    py: { xs: 1, sm: 1.5 },
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    fontWeight: 'bold',
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: theme.shadows[8],
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Back to Home
                            </Button>
                            
                            <Button
                                fullWidth
                                variant="outlined"
                                size="large"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(-1)}
                                sx={{
                                    py: { xs: 1, sm: 1.5 },
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    fontWeight: 'bold',
                                    borderColor: alpha(theme.palette.primary.main, 0.5),
                                    '&:hover': {
                                        borderColor: theme.palette.primary.main,
                                        transform: 'translateY(-2px)',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Go Back
                            </Button>
                        </Box>

                        {/* Help Section */}
                        <Box
                            sx={{
                                mt: { xs: 3, sm: 4 },
                                pt: { xs: 2, sm: 3 },
                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                            }}
                        >
                            <Button
                                startIcon={<Help />}
                                onClick={() => window.location.href = '/contact'}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                    },
                                }}
                            >
                                Need Help? Contact Support
                            </Button>
                        </Box>

                        {/* Decorative Element */}
                        <Box
                            component={motion.div}
                            initial={{ width: 0 }}
                            animate={{ width: '100%' }}
                            transition={{ duration: 1, delay: 0.5 }}
                            sx={{
                                height: 2,
                                background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                                mt: 3,
                            }}
                        />
                    </Paper>
                </motion.div>
            </Container>
        </Box>
    );
};

export default NotFoundPage;